import { useRef, useCallback } from "react";
import { WavRecorder } from "wavtools";
import { Socket } from "socket.io-client";
import { NoiseAnalyzer, rmsAndDbfsInt16 } from "../utils/noiseAnalysis";
import { forceStopAllMediaStreams } from "../utils/mediaStreamCleanup";
import {
  END_OF_UTTERANCE_SILENCE_MS,
  scheduleTrailingSilenceChunks,
  type TrailingSilenceCancel,
} from "../utils/trailingSilence";

const SAMPLES_PER_SUBCHUNK = 480;
const MAX_QUEUE_SIZE = 5;
const VAD_END_OF_UTTERANCE_SILENCE_MS = 700;

interface UseAudioRecordingProps {
  user_id?: string;
  avatarId?: string;
  voiceId?: string;
  socketRef: React.MutableRefObject<Socket | null>;
  audioStreamRef: React.MutableRefObject<MediaStream | null>;
  isMuted: boolean;
  isRecording: boolean;
  setIsRecording: (recording: boolean) => void;
  setAudioStream: (stream: MediaStream | null) => void;
  isProcessingPausedRef: React.MutableRefObject<boolean>;
  noiseAnalyzerRef: React.MutableRefObject<NoiseAnalyzer>;
  noiseWarningShownRef: React.MutableRefObject<boolean>;
  noiseCheckDoneRef: React.MutableRefObject<boolean>;
  setIsWarningError: (error: boolean) => void;
  setIsMicDisabledByWarning: (disabled: boolean) => void;
  setWarningMessage: (message: string | null) => void;
}

export const useAudioRecording = ({
  user_id,
  avatarId,
  voiceId,
  socketRef,
  audioStreamRef,
  isMuted,
  isRecording,
  setIsRecording,
  setAudioStream,
  isProcessingPausedRef,
  noiseAnalyzerRef,
  noiseWarningShownRef,
  noiseCheckDoneRef,
  setIsWarningError,
  setIsMicDisabledByWarning,
  setWarningMessage,
}: UseAudioRecordingProps) => {
  const wavRecorderRef = useRef<WavRecorder | null>(null);
  const chunkQueue = useRef<Int16Array[]>([]);
  const isCurrentlySendingRef = useRef(false);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cancelTrailingSilenceRef = useRef<TrailingSilenceCancel | null>(null);
  const isMutedRef = useRef(isMuted);

  isMutedRef.current = isMuted;

  const sendChunkForce = useCallback(
    (chunk: Int16Array) => {
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit("audio_chunk", {
          user_id,
          chunk,
          heygen_mode: true,
          avatar_id: avatarId,
          voice_id: voiceId,
        });
      }
    },
    [user_id, avatarId, voiceId, socketRef],
  );

  const onNewChunk = useCallback(
    (chunk: Int16Array) => {
      if (chunkQueue.current.length < MAX_QUEUE_SIZE) {
        chunkQueue.current.push(chunk);
      } else {
        const oldestChunk = chunkQueue.current.shift();
        if (
          oldestChunk &&
          isCurrentlySendingRef.current &&
          !isMutedRef.current
        ) {
          sendChunkForce(oldestChunk);
        }
        chunkQueue.current.push(chunk);
      }
    },
    [sendChunkForce],
  );

  const flushQueue = useCallback(() => {
    while (chunkQueue.current.length > 0) {
      const oldestChunk = chunkQueue.current.shift();
      if (oldestChunk && !isMutedRef.current) {
        sendChunkForce(oldestChunk);
      }
    }
  }, [sendChunkForce]);

  const sendAudio = useCallback(
    (buffer: Int16Array) => {
      if (isProcessingPausedRef.current) {
        return;
      }

      const chunk = new Int16Array(buffer);

      for (let i = 0; i < chunk.length; i += SAMPLES_PER_SUBCHUNK) {
        const subChunk = chunk.slice(i, i + SAMPLES_PER_SUBCHUNK);

        // --- Noise analysis (also calculates dbfs internally) ---
        // While muted (roleplay PTT idle), keep windows but suppress warn evaluation
        // so the one-shot warning is not consumed before the user presses the mic.
        const noiseResult = noiseAnalyzerRef.current.processChunk(
          subChunk,
          isProcessingPausedRef.current || isMutedRef.current,
        );

        // --- Metrics for this subChunk (fast) - for VAD ---
        const { dbfs } = rmsAndDbfsInt16(subChunk);

        // --- VAD-lite: dynamic silence threshold (adapts to background noise) ---
        const vadSilenceDbfs = noiseAnalyzerRef.current.getVadSilenceDbfs();
        const isSilent = dbfs < vadSilenceDbfs;

        if (noiseResult?.shouldWarn) {
          noiseWarningShownRef.current = true;
          setIsWarningError(true);
          setIsMicDisabledByWarning(false);
          setWarningMessage(noiseResult.message);
        }

        // --- Next — VAD / queue logic as was ---
        if (isProcessingPausedRef.current) {
          continue;
        }

        if (isMutedRef.current) {
          continue;
        } else {
          if (isSilent) {
            if (isCurrentlySendingRef.current && !silenceTimeoutRef.current) {
              silenceTimeoutRef.current = setTimeout(() => {
                flushQueue();
                isCurrentlySendingRef.current = false;
                silenceTimeoutRef.current = null;
              }, VAD_END_OF_UTTERANCE_SILENCE_MS);
            }
          } else {
            if (!isCurrentlySendingRef.current) {
              flushQueue();
              isCurrentlySendingRef.current = true;
            } else if (silenceTimeoutRef.current) {
              clearTimeout(silenceTimeoutRef.current);
              silenceTimeoutRef.current = null;
            }
          }
        }

        if (isCurrentlySendingRef.current) {
          onNewChunk(subChunk);
        }
      }
    },
    [
      flushQueue,
      onNewChunk,
      isProcessingPausedRef,
      noiseAnalyzerRef,
      noiseWarningShownRef,
      setIsWarningError,
      setIsMicDisabledByWarning,
      setWarningMessage,
    ],
  );

  const userMicDataHandler = useCallback(
    (data: { mono: Int16Array; raw: Int16Array }) => {
      if (isProcessingPausedRef.current) {
        return;
      }
      if (socketRef.current && socketRef.current.connected) {
        sendAudio(data.mono);
      }
    },
    [sendAudio, isProcessingPausedRef, socketRef],
  );

  const ensureRecorderActive = useCallback(async () => {
    if (!wavRecorderRef.current) {
      const wavRecorder = new WavRecorder({ sampleRate: 24000 });
      wavRecorderRef.current = wavRecorder;
      await wavRecorder.begin();
      await wavRecorder.record(userMicDataHandler);
      return;
    }

    try {
      const status = wavRecorderRef.current.getStatus?.();
      if (status !== "recording") {
        // @ts-ignore
        await wavRecorderRef.current.audioContext?.resume?.();

        await wavRecorderRef.current.begin();
        await wavRecorderRef.current.record(userMicDataHandler);
      }
    } catch (e) {
      try {
        wavRecorderRef.current = null as any;
        const wavRecorder = new WavRecorder({ sampleRate: 24000 });
        wavRecorderRef.current = wavRecorder;
        await wavRecorder.begin();
        await wavRecorder.record(userMicDataHandler);
      } catch (err) {
        console.error("[ensureRecorderActive] failed reinit:", err);
        wavRecorderRef.current = null;
      }
    }
  }, [userMicDataHandler]);

  const startAudioRecording = useCallback(async () => {
    if (isRecording) return;

    if (!audioStreamRef.current) return;

    try {
      chunkQueue.current = [];
      isCurrentlySendingRef.current = false;
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      noiseCheckDoneRef.current = false;
      noiseWarningShownRef.current = false;
      noiseAnalyzerRef.current.reset();

      await ensureRecorderActive();

      setIsRecording(true);

      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit("user_ready", {
          user_id,
          heygen_mode: true,
          avatar_id: avatarId,
          voice_id: voiceId,
        });
      }
    } catch (err) {
      wavRecorderRef.current = null;
      throw new Error("Failed to start recording");
    }
  }, [
    isRecording,
    avatarId,
    voiceId,
    user_id,
    setIsRecording,
    ensureRecorderActive,
    audioStreamRef,
    socketRef,
    noiseCheckDoneRef,
    noiseWarningShownRef,
    noiseAnalyzerRef,
  ]);

  const cancelTrailingSilence = useCallback(() => {
    if (cancelTrailingSilenceRef.current) {
      cancelTrailingSilenceRef.current();
      cancelTrailingSilenceRef.current = null;
    }
  }, []);

  const emitUserMessageEnd = useCallback(
    (reason: string) => {
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit("user_message_end", {
          user_id,
          heygen_mode: true,
          reason,
        });
      }
    },
    [socketRef, user_id],
  );

  const stopAudioRecording = useCallback(
    async (
      shouldStopTracks = false,
      options?: {
        finalizeTurn?: boolean;
        reason?: string;
        trailingSilenceMs?: number;
      },
    ) => {
      try {
        const wasRecording =
          wavRecorderRef.current &&
          wavRecorderRef.current.getStatus &&
          wavRecorderRef.current.getStatus() === "recording";
        const hadUserAudio =
          isCurrentlySendingRef.current || chunkQueue.current.length > 0;

        if (shouldStopTracks) {
          const recorder = wavRecorderRef.current;
          if (recorder) {
            const rs = (recorder as any).stream;
            if (rs instanceof MediaStream)
              rs.getTracks().forEach((t) => {
                try {
                  if (t.readyState !== "ended") t.stop();
                } catch (_) {}
              });
            if (recorder.getStatus?.() !== "ended") {
              try {
                await Promise.race([
                  recorder.end(),
                  new Promise((_, r) => setTimeout(r, 1500)),
                ]);
              } catch (_) {}
            }
            wavRecorderRef.current = null;
          }
          forceStopAllMediaStreams(audioStreamRef, wavRecorderRef);
        }

        setIsRecording(false);

        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }

        if (wasRecording && isCurrentlySendingRef.current) {
          flushQueue();
        }

        const trailingSilenceMs = options?.trailingSilenceMs ?? 0;
        const shouldFinalizeWithSilence =
          !shouldStopTracks &&
          options?.finalizeTurn &&
          hadUserAudio &&
          trailingSilenceMs > 0;

        if (shouldFinalizeWithSilence) {
          cancelTrailingSilence();

          const currentStream = audioStreamRef.current;
          if (currentStream) {
            currentStream.getTracks().forEach((track) => {
              track.enabled = false;
            });
          }

          isCurrentlySendingRef.current = true;

          cancelTrailingSilenceRef.current = scheduleTrailingSilenceChunks(
            (chunk) => {
              sendChunkForce(chunk);
            },
            () => {
              cancelTrailingSilenceRef.current = null;
              flushQueue();
              isCurrentlySendingRef.current = false;
              chunkQueue.current = [];
              emitUserMessageEnd(options?.reason || "mute");
            },
            trailingSilenceMs,
          );
          return;
        }

        isCurrentlySendingRef.current = false;
        chunkQueue.current = [];

        if (shouldStopTracks) {
          forceStopAllMediaStreams(audioStreamRef, wavRecorderRef);
        } else {
          const currentStream = audioStreamRef.current;
          if (currentStream) {
            currentStream.getTracks().forEach((track) => {
              track.enabled = false;
            });
          }
        }

        if (
          socketRef.current &&
          socketRef.current.connected &&
          (shouldStopTracks || options?.finalizeTurn) &&
          hadUserAudio
        ) {
          emitUserMessageEnd(
            options?.reason || (shouldStopTracks ? "cleanup" : "mute"),
          );
        }
      } catch (err) {
        if (shouldStopTracks) {
          wavRecorderRef.current = null;
        }
        setIsRecording(false);
      }
    },
    [
      user_id,
      setIsRecording,
      flushQueue,
      audioStreamRef,
      socketRef,
      wavRecorderRef,
      cancelTrailingSilence,
      emitUserMessageEnd,
      sendChunkForce,
    ],
  );

  return {
    startAudioRecording,
    stopAudioRecording,
    cancelTrailingSilence,
    chunkQueue,
    isCurrentlySendingRef,
    silenceTimeoutRef,
    wavRecorderRef,
  };
};
